import { MultiRoomGuestField } from '@common/components/BookingForms/GuestField/MultiRoomGuestField';
import { useTranslations } from '@lang';
import { FC, useEffect } from 'react';
import { Field, FieldArray } from 'redux-form';
import { useRouter } from 'shared-routing';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuests } from '../../../../components/AddExistingGuests';
import { SectionWrapper } from '../../../../components/BookingForms/Section';
import { Button } from '../../../../components/Button';
import { TextareaField } from '../../../../components/FormElements';
import Portal from '../../../../components/Portal';
import { StickyBarContent } from '../../../../components/StickyBar';
import { Routes } from '../../../../constants';
import { NotesWrapper, StyledDateRangeIndicator } from './MultiRoomGuestDetailsStep.styles';
import {
  MultiRoomGuestDetailsStepProps,
  MultiRoomGuestDetailsStepReduxProps,
  RoomPartial,
} from './MultiRoomGuestDetailsStep.types';

const routesMap = {
  room: Routes.ROOM_DETAIL,
  rate: Routes.RATE_DETAIL,
};

export const MultiRoomGuestDetailsStep = ({
  basketGuests,
  loggedGuest,
  handleSubmit,
  stickyBarId,
  selectedEntity,
  change,
  selectedGuests = [],
  valid,
  submitting,
  datePartyData,
  isSignInModalOpen = false,
  handleSignInModalOpen,
  goToNextStep,
  addToBasket,
  metaEntity,
  availableExtras,
}: MultiRoomGuestDetailsStepProps & MultiRoomGuestDetailsStepReduxProps) => {
  const { t } = useTranslations();
  const router = useRouter();

  useEffect(() => {
    if (selectedEntity) {
      return;
    }

    router.push({
      pathname: routesMap[metaEntity.type],
      query: {
        id: metaEntity.id,
      },
    });
  }, [selectedEntity, router, metaEntity.type, metaEntity.id]);

  const hasExtras = availableExtras?.length;

  const onSubmit = ({ guests, note }) => {
    if (hasExtras) {
      goToNextStep();
    } else {
      addToBasket({ guestDetails: guests, note }).then(() => goToNextStep());
    }
  };

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        {datePartyData.rooms.map((r: RoomPartial, index: number) => {
          return (
            <>
              {index === 0 && (
                <FieldArray
                  name="guests"
                  component={AddExistingGuests}
                  selectedGuests={selectedGuests}
                  existingGuests={basketGuests as Guest[]}
                  minimumGuests={r.adults}
                  maximumGuests={r.adults}
                  isGenderSpecific={false}
                  guest={loggedGuest}
                />
              )}
              <FieldArray
                name="guests"
                component={MultiRoomGuestField as FC}
                props={{
                  fieldChange: change,
                  minimumGuests: r.adults,
                  maximumGuests: r.adults,
                  withGenderSelect: false,
                  withLeadGuest: index === 0,
                  handleSignInModalOpen,
                  isSignInModalOpen,
                  room: r,
                }}
              />
              {r.children ? <>+{t('basket.children', { children: r.children })}</> : null}
            </>
          );
        })}

        <NotesWrapper>
          <Field
            isFullWidth
            component={TextareaField}
            labelWidth="150px"
            name="note"
            label={t('rooms.notes')}
            id="additional-notes"
          />
        </NotesWrapper>
      </SectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={datePartyData.fromDate}
              toDate={datePartyData.toDate}
            />
          }
          displayedPrice={selectedEntity?.pricePerStay || null}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!valid || submitting}
            >
              {t(hasExtras ? 'spa.package.nextStep' : 'product.ctaButton')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
